/**
 * Intel.js v4.4.13 — Competitive Intelligence
 *
 * Dashboard agregado sobre oportunidades_placsp:
 *   - KPIs globales
 *   - Top adjudicatarios (competencia a mapear)
 *   - Top órganos contratantes (dónde publican nuestros sectores)
 *   - Heatmap por región
 *   - Evolución mensual
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/services/api";
import {
  Brain,
  TrendingUp,
  MapPin,
  Building2,
  Target,
  Euro,
  Award,
  BarChart3,
  RefreshCw,
  FilterX,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { toast } from "sonner";
import AdjudicatarioHistorialModal from "@/components/AdjudicatarioHistorialModal";

function formatEUR(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} B€`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} K€`;
  return `${n.toFixed(0)} €`;
}

const PERIODOS = [
  { value: "30d", label: "Último mes", dias: 30 },
  { value: "90d", label: "Últimos 3 meses", dias: 90 },
  { value: "180d", label: "Últimos 6 meses", dias: 180 },
  { value: "365d", label: "Último año", dias: 365 },
  { value: "all", label: "Todo el histórico", dias: null },
];

export default function Intel() {
  const [sector, setSector] = useState("todos");
  const [region, setRegion] = useState("");
  const [periodo, setPeriodo] = useState("180d");

  const [summary, setSummary] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [trending, setTrending] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tiposSrs, setTiposSrs] = useState([]);

  // Modal histórico del adjudicatario clickado
  const [adjSeleccionado, setAdjSeleccionado] = useState(null);

  // ¿Hay filtros activos?
  const hasFiltros = (sector && sector !== "todos") || region || periodo !== "180d";

  const clearFiltros = () => {
    setSector("todos");
    setRegion("");
    setPeriodo("180d");
  };

  // Construir params según filtros
  const params = useMemo(() => {
    const p = {};
    if (sector && sector !== "todos") p.sector = sector;
    if (region) p.region = region;
    const def = PERIODOS.find((x) => x.value === periodo);
    if (def?.dias) {
      const d = new Date();
      d.setDate(d.getDate() - def.dias);
      p.desde = d.toISOString().slice(0, 10);
    }
    return p;
  }, [sector, region, periodo]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rSum, rRank, rHeat, rTrend] = await Promise.all([
        api.get("/intel/summary", { params }),
        api.get("/intel/rankings", { params }),
        api.get("/intel/heatmap", { params: { sector: params.sector, desde: params.desde } }),
        api.get("/intel/trending", {
          params: { sector: params.sector, region: params.region, meses: 12 },
        }),
      ]);
      setSummary(rSum.data);
      setRankings(rRank.data);
      setHeatmap(rHeat.data);
      setTrending(rTrend.data);
    } catch (e) {
      console.error(e);
      toast.error("Error cargando inteligencia");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    api.get("/oportunidades/tipos-srs").then((r) => setTiposSrs(r.data || [])).catch(() => {});
  }, []);

  // Datos preparados para Recharts
  const trendingChartData = useMemo(() => {
    return (trending?.timeline || []).map((t) => ({
      mes: t._id,
      contratos: t.count,
      valorM: Math.round((t.valor || 0) / 100_000) / 10, // M€
      criticas: t.criticas,
      ue: t.con_ue,
    }));
  }, [trending]);

  const heatmapChartData = useMemo(() => {
    return (heatmap?.regiones || []).slice(0, 15).map((r) => ({
      region: r._id,
      contratos: r.count,
      valorM: Math.round((r.valor_total || 0) / 100_000) / 10,
    }));
  }, [heatmap]);

  // v4.4.19: lista única de regiones para el selector
  // Se recalcula con el último heatmap cargado; ordenadas alfabéticamente.
  const regionesDisponibles = useMemo(() => {
    const set = new Set();
    (heatmap?.regiones || []).forEach((r) => {
      if (r._id && typeof r._id === "string") set.add(r._id.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [heatmap]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display theme-text flex items-center gap-2">
            <Brain className="w-7 h-7 text-teal-400" />
            Inteligencia
          </h1>
          <p className="theme-text-secondary text-sm">
            Competitive intel: quién gana, dónde, cómo y cuándo
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-400/15 text-teal-400 border border-teal-400/40 hover:shadow-glow-primary text-sm font-medium"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <Card className="theme-bg-secondary border-white/10">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger
                className="w-[240px] theme-bg-tertiary theme-text h-9 text-sm"
                style={{ border: "1px solid var(--theme-border)" }}
              >
                <SelectValue placeholder="Sector SRS" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los sectores</SelectItem>
                {tiposSrs.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* v4.4.19: Región como Select con lista de regiones reales */}
            <Select
              value={region || "todas"}
              onValueChange={(v) => setRegion(v === "todas" ? "" : v)}
            >
              <SelectTrigger
                className="w-[220px] theme-bg-tertiary theme-text h-9 text-sm"
                style={{ border: "1px solid var(--theme-border)" }}
              >
                <MapPin className="w-3.5 h-3.5 mr-1 text-slate-500" />
                <SelectValue placeholder="Todas las regiones" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="todas">Todas las regiones</SelectItem>
                {regionesDisponibles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger
                className="w-[220px] theme-bg-tertiary theme-text h-9 text-sm"
                style={{ border: "1px solid var(--theme-border)" }}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODOS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Botón limpiar siempre visible; activo solo si hay filtros */}
            <button
              onClick={clearFiltros}
              disabled={!hasFiltros}
              className={`flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium border transition-colors ${
                hasFiltros
                  ? "text-red-500 border-red-500/40 hover:bg-red-500/10 cursor-pointer"
                  : "theme-text-muted opacity-50 cursor-not-allowed"
              }`}
              style={!hasFiltros ? { border: "1px solid var(--theme-border)" } : {}}
              title={hasFiltros
                ? "Limpiar filtros y volver al estado por defecto"
                : "No hay filtros activos"}
            >
              <FilterX className="w-4 h-4" />
              Limpiar
            </button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={<Target className="w-5 h-5 text-teal-400" />}
          label="Oportunidades"
          value={summary?.total_oportunidades?.toLocaleString("es-ES") || "-"}
          sub={`${summary?.adjudicatarios_unicos || 0} adjudicatarios`}
        />
        <KpiCard
          icon={<Euro className="w-5 h-5 text-green-400" />}
          label="Valor total"
          value={formatEUR(summary?.valor_total)}
          sub={`Medio: ${formatEUR(summary?.importe_medio)}`}
        />
        <KpiCard
          icon={<Building2 className="w-5 h-5 text-purple-400" />}
          label="Órganos contratantes"
          value={summary?.organos_unicos?.toLocaleString("es-ES") || "-"}
          sub="publican en este sector/región"
        />
        <KpiCard
          icon={<TrendingUp className="w-5 h-5 text-blue-400" />}
          label="Con fondos UE"
          value={summary?.con_fondos_ue?.toLocaleString("es-ES") || "-"}
          sub={`${summary?.con_fondos_ue_pct || 0}% del total`}
        />
      </div>

      {/* Trending */}
      <Card className="theme-bg-secondary border-white/10">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-teal-400" />
            Evolución mensual (contratos + valor M€)
          </h3>
          {trendingChartData.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">Sin datos para los filtros actuales</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendingChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2a42" />
                <XAxis dataKey="mes" stroke="#8896ab" style={{ fontSize: 11 }} />
                <YAxis yAxisId="left" stroke="#0dd3c5" style={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#10b981" style={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0c1220",
                    border: "1px solid #1e2a42",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#e8ecf4" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="contratos"
                  stroke="#0dd3c5"
                  strokeWidth={2}
                  name="Contratos"
                  dot={{ r: 3 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="valorM"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Valor (M€)"
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top adjudicatarios */}
        <Card className="theme-bg-secondary border-white/10">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              Top 15 adjudicatarios (por valor ganado)
            </h3>
            <div className="space-y-1 max-h-[500px] overflow-y-auto">
              {(rankings?.top_adjudicatarios || []).map((a, i) => (
                <button
                  key={a._id}
                  onClick={() => setAdjSeleccionado(a)}
                  className="w-full text-left p-2 rounded theme-bg-tertiary transition-colors"
                  style={{ border: "1px solid var(--theme-border)" }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <span className="text-[11px] theme-text-muted font-mono w-5 shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm theme-text truncate">{a._id}</p>
                        <div className="flex items-center gap-2 text-[11px] theme-text-muted mt-0.5 flex-wrap">
                          <span>{a.count} contratos</span>
                          {a.regiones?.[0] && <span>· {a.regiones[0]}</span>}
                          {a.es_partner_srs && (
                            <Badge className="bg-teal-500/20 text-teal-500 border-teal-500/40 text-[9px] px-1.5 py-0">
                              PARTNER
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-sm font-semibold text-green-500 shrink-0">
                      {formatEUR(a.valor_total)}
                    </span>
                  </div>
                </button>
              ))}
              {(!rankings?.top_adjudicatarios || rankings.top_adjudicatarios.length === 0) && (
                <p className="theme-text-muted text-sm py-4 text-center">Sin datos</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top órganos */}
        <Card className="theme-bg-secondary border-white/10">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-purple-500" />
              Top 15 órganos contratantes (dónde publican)
            </h3>
            <div className="space-y-1 max-h-[500px] overflow-y-auto">
              {(rankings?.top_organos || []).map((o, i) => (
                <div
                  key={o._id}
                  className="p-2 rounded theme-bg-tertiary"
                  style={{ border: "1px solid var(--theme-border)" }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <span className="text-[11px] theme-text-muted font-mono w-5 shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm theme-text truncate">{o._id}</p>
                        <p className="text-[11px] theme-text-muted mt-0.5">
                          {o.count} contratos · medio {formatEUR(o.importe_medio)}
                        </p>
                      </div>
                    </div>
                    <span className="font-mono text-sm font-semibold text-purple-500 shrink-0">
                      {formatEUR(o.valor_total)}
                    </span>
                  </div>
                </div>
              ))}
              {(!rankings?.top_organos || rankings.top_organos.length === 0) && (
                <p className="theme-text-muted text-sm py-4 text-center">Sin datos</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Heatmap regional */}
      <Card className="theme-bg-secondary border-white/10">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-amber-400" />
            Top 15 regiones (por valor)
          </h3>
          {heatmapChartData.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={heatmapChartData}
                layout="vertical"
                margin={{ left: 100, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2a42" horizontal={false} />
                <XAxis type="number" stroke="#8896ab" style={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="region" stroke="#8896ab" style={{ fontSize: 11 }} width={100} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0c1220",
                    border: "1px solid #1e2a42",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#e8ecf4" }}
                  formatter={(v, n) => (n === "valorM" ? `${v} M€` : v)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="contratos" fill="#0dd3c5" name="Contratos" />
                <Bar dataKey="valorM" fill="#f0a230" name="Valor (M€)" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Top CPVs */}
      <Card className="theme-bg-secondary border-white/10">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-400" />
            Top 10 CPVs más frecuentes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(rankings?.top_cpvs || []).map((c) => (
              <div
                key={c._id}
                className="p-2 rounded theme-bg-tertiary flex items-start justify-between gap-2"
                style={{ border: "1px solid var(--theme-border)" }}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm text-teal-400">{c._id}</p>
                  {c.desc && (
                    <p className="text-[11px] theme-text-muted truncate">{c.desc}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold theme-text">{c.count}</p>
                  <p className="text-[11px] text-green-500 font-mono">
                    {formatEUR(c.valor_total)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Modal histórico del adjudicatario */}
      <AdjudicatarioHistorialModal
        open={!!adjSeleccionado}
        onClose={() => setAdjSeleccionado(null)}
        adjudicatario={adjSeleccionado?._id}
        nif={adjSeleccionado?.nif}
      />
    </div>
  );
}

function KpiCard({ icon, label, value, sub }) {
  return (
    <Card className="theme-bg-secondary border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs theme-text-muted uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold font-mono theme-text mt-0.5">{value}</p>
            {sub && <p className="text-xs theme-text-muted mt-1 truncate">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-lg bg-slate-800/50 flex items-center justify-center shrink-0">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
